import { StencilProvider, ThemeMode } from '@amzn/stencil-react-components/context';
import { applyTheme } from '@amzn/awsui-components-core/theming';
import defaultStencilTokens from '@amzn/stencil-design-tokens/js/web/theme/stencilTheme';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { I18nProvider } from '@amzn/awsui-components-core/i18n';
import enMessages from '@amzn/awsui-components-core/i18n/messages/all.en';
import { Mode } from '@amzn/awsui-global-styles';

import { AgChartsEnterpriseModule } from 'ag-charts-enterprise';
import { ModuleRegistry } from 'ag-grid-community';
import { AllEnterpriseModule, IntegratedChartsModule } from 'ag-grid-enterprise';

import App from './components/App';
import AppInitWrapper from './components/AppInitWrapper';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { APP_THEME, FONT_FAMILY_BASE } from './constants/ThemeConstants';

import './index.scss';

// Apply Cloudscape theme (font family + button overrides)
applyTheme({ theme: APP_THEME });

// Override Stencil's default font (Amazon Ember) with Ember Modern Text to match Cloudscape theming
const STENCIL_THEME = {
  ...defaultStencilTokens,
  ':root': {
    ...defaultStencilTokens[':root'],
    '--st-dt-text-family-default': FONT_FAMILY_BASE,
  },
};

// Register AG Grid Enterprise modules
ModuleRegistry.registerModules([AllEnterpriseModule, IntegratedChartsModule.with(AgChartsEnterpriseModule)]);

const StencilWithTheme: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { mode } = useTheme();
  const themeMode = mode === Mode.Dark ? ThemeMode.Dark : ThemeMode.Light;
  return (
    <StencilProvider themeMode={themeMode} theme={STENCIL_THEME}>
      {children}
    </StencilProvider>
  );
};

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element not found. Failed to render React application.');
}

const root = createRoot(container);
root.render(
  // <React.StrictMode>
  <ThemeProvider>
    <StencilWithTheme>
      <Provider store={store}>
        <AppInitWrapper>
          <React.Suspense>
            <I18nProvider locale={'en'} messages={[enMessages]}>
              <App />
            </I18nProvider>
          </React.Suspense>
        </AppInitWrapper>
      </Provider>
    </StencilWithTheme>
  </ThemeProvider>
  // </React.StrictMode>
);
